// --- ARQUIVO COMPLETO ATUALIZADO: frontend/src/pages/championships/ChampionshipDetail.jsx ---

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import championshipService from '../../services/championshipService';
import teamService from '../../services/teamService';
import mataMataService from '../../services/mataMataService';
import useAuth from '../../hooks/useAuth';
import './ChampionshipDetail.css';
import backgroundImg from '../../assets/background.png';
import ChampionshipMediaTab from './ChampionshipMediaTab';
import RoundStats from './RoundStats';
import SponsorList from './SponsorList';
import ChampionshipNews from './ChampionshipNews';
import ChampionshipManagers from './ChampionshipManagers';
import ChampionshipRegulations from './ChampionshipRegulations';
import MarketList from '../../components/MarketList';
import ManageGroups from './ManageGroups';
import PropTypes from 'prop-types';

// ✅ IMPORTA O NOVO COMPONENTE
import MatchesToday from './MatchesToday';
import PenaltyTab from './PenaltyTab';

const ChevronLeftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Data inválida' : date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.error('Erro ao formatar data:', e);
    return 'Data inválida';
  }
};

const formatDateOnly = (dateString) => {
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Data inválida' : date.toLocaleDateString('pt-BR');
  } catch (e) {
    console.error('Erro ao formatar data:', e);
    return 'Data inválida';
  }
};

const normalizeStatus = (status) => {
  if (!status) return 'indefinido';
  return status
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
};

const hasGroups = (format) => {
  if (!format) return false;
  const lowerFormat = format.toLowerCase();
  return lowerFormat.includes('fase de grupos') || lowerFormat === 'formato personalizado';
};

const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="loading-spinner"><div></div><div></div><div></div><div></div></div>
    <p>Carregando dados do campeonato...</p>
  </div>
);

// ✅ COMPONENTE DE CONTAGEM REGRESSIVA ATUALIZADO
const ChampionshipStatusDisplay = ({ championship, currentStatus, firstMatchInfo, relevantStartInfo }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    // Usar a data relevante (primeira partida OU início do campeonato)
    let targetDate = null;
    
    if (firstMatchInfo?.date && !isNaN(new Date(firstMatchInfo.date).getTime())) {
      targetDate = new Date(firstMatchInfo.date);
    } else if (championship?.startDate) {
      targetDate = new Date(championship.startDate);
    }
    
    if (!targetDate) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const difference = targetDate - new Date();
    
    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  // Só atualiza o timer se o campeonato estiver agendado
  useEffect(() => {
    if (currentStatus !== 'scheduled') {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [currentStatus, firstMatchInfo, championship]);

  // Estado 1: Campeonato finalizado
  if (currentStatus === 'completed') {
    return (
      <div className="championship-completed">
        <div className="completed-subtitle">A competição chegou ao fim. Parabéns a todos os participantes!</div>
        <div className="completed-details">
          <div className="completed-detail">
            <span className="detail-label">Status:</span>
            <span className="detail-value finalizado">Finalizado</span>
          </div>
          {championship?.endDate && (
            <div className="completed-detail">
              <span className="detail-label">Término oficial:</span>
              <span className="detail-value">{formatDate(championship.endDate)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Estado 2: Campeonato em andamento
  if (currentStatus === 'in-progress') {
    return (
      <div className="championship-started">
        <div className="started-subtitle">
          O campeonato está em andamento desde {relevantStartInfo?.date ? formatDate(relevantStartInfo.date) : 'data indefinida'}.
        </div>
        <div className="live-indicator">
          <span className="live-dot" aria-hidden="true"></span>
          Ao vivo
        </div>
      </div>
    );
  }

  // Estado 3: Contagem regressiva (ainda não começou)
  return (
    <div className="countdown-container">
      <div className="countdown-header">
        <p className="countdown-subtitle">
          Para {relevantStartInfo?.type || 'o início do campeonato'}
        </p>
      </div>
      <div className="countdown-grid">
        <div className="countdown-unit">
          <div className="countdown-value">{String(timeLeft.days).padStart(2, '0')}</div>
          <div className="countdown-label">Dias</div>
        </div>
        <div className="countdown-unit">
          <div className="countdown-value">{String(timeLeft.hours).padStart(2, '0')}</div>
          <div className="countdown-label">Horas</div>
        </div>
        <div className="countdown-unit">
          <div className="countdown-value">{String(timeLeft.minutes).padStart(2, '0')}</div>
          <div className="countdown-label">Minutos</div>
        </div>
        <div className="countdown-unit">
          <div className="countdown-value">{String(timeLeft.seconds).padStart(2, '0')}</div>
          <div className="countdown-label">Segundos</div>
        </div>
      </div>
      <div className="countdown-footer">
        <p>
          {relevantStartInfo?.date ? 
            `Início previsto: ${formatDate(relevantStartInfo.date)}` : 
            'Data de início não definida'}
        </p>
      </div>
    </div>
  );
};

// ✅ COMPONENTE: PÓDIO DOS COLOCADOS
const ChampionshipPodium = ({ rankings }) => {
  if (!rankings || (!rankings.champion && !rankings.runnerUp && !rankings.thirdPlace)) {
    return null;
  }

  return (
    <div className="championship-podium card-style">
      <h3>Pódio do campeonato</h3>
      <div className="podium-grid">
        {/* 2º lugar - Vice-campeão */}
        <div className="podium-place second-place">
          <div className="podium-medal" aria-hidden="true">2º</div>
          <div className="podium-team">
            {rankings.runnerUp?.name || "Vice-campeão a definir"}
          </div>
          <div className="podium-label">Vice-campeão</div>
          {rankings.runnerUp && (
            <div className="team-badge-small">
              <img 
                src={rankings.runnerUp.badge || '/default-team-logo.png'} 
                alt={rankings.runnerUp.name}
                onError={(e) => e.target.src = '/default-team-logo.png'}
              />
            </div>
          )}
        </div>
        
        {/* 1º lugar - Campeão */}
        <div className="podium-place first-place">
          <div className="podium-medal" aria-hidden="true">1º</div>
          <div className="podium-team champion-team">
            {rankings.champion?.name || "Campeão a definir"}
          </div>
          <div className="podium-label">Campeão</div>
          {rankings.champion && (
            <div className="team-badge-small">
              <img 
                src={rankings.champion.badge || '/default-team-logo.png'} 
                alt={rankings.champion.name}
                onError={(e) => e.target.src = '/default-team-logo.png'}
              />
            </div>
          )}
        </div>
        
        {/* 3º lugar */}
        <div className="podium-place third-place">
          <div className="podium-medal" aria-hidden="true">3º</div>
          <div className="podium-team">
            {rankings.thirdPlace?.name || "3º lugar a definir"}
          </div>
          <div className="podium-label">3º lugar</div>
          {rankings.thirdPlace && (
            <div className="team-badge-small">
              <img 
                src={rankings.thirdPlace.badge || '/default-team-logo.png'} 
                alt={rankings.thirdPlace.name}
                onError={(e) => e.target.src = '/default-team-logo.png'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ percentage, color = 'var(--accent)' }) => {
  const value = Math.max(0, Math.min(100, percentage));

  return (
    <div
      className="progress-bar-container"
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="progress-bar-fill"
        style={{ width: `${value}%`, backgroundColor: color }}
      />
    </div>
  );
};

function ChampionshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loadingAuth } = useAuth();

  const [championship, setChampionship] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [linkCopiedMessage, setLinkCopiedMessage] = useState('');
  
  const [rankings, setRankings] = useState({
    champion: null,
    runnerUp: null,
    thirdPlace: null
  });
  const [loadingRankings, setLoadingRankings] = useState(false);

  // Ao abrir (ou trocar de) campeonato, a página começa no topo.
  // O segundo efeito repete o scroll quando o conteúdo termina de carregar,
  // já que o navegador pode restaurar a posição da página anterior nesse momento.
  const scrolledForId = useRef(null);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);
  useEffect(() => {
    if (!loading && championship && scrolledForId.current !== id) {
      scrolledForId.current = id;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [loading, championship, id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [championshipData, teamsData, matchesDataResponse] = await Promise.all([
        championshipService.getById(id),
        teamService.getTeamsByChampionship(id),
        championshipService.getMatchesByChampionship(id)
      ]);
      
      const championshipPayload = championshipData?.data || championshipData;
      if (championshipPayload?._id) {
          setChampionship(championshipPayload);
      } else {
          throw new Error('Dados do campeonato inválidos');
      }

      setTeams(teamsData.data || teamsData || []);
      const rawMatches = matchesDataResponse.data || matchesDataResponse;
      setMatches(Array.isArray(rawMatches) ? rawMatches : []);
      setError(null);
    } catch (err) {
      console.error('Falha ao carregar dados:', err);
      setError(err.message || 'Falha ao carregar os dados do campeonato');
      setChampionship(null);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ✅ FUNÇÃO: Buscar rankings do mata-mata
  const fetchRankings = useCallback(async () => {
    if (!id) return;
    
    setLoadingRankings(true);
    try {
      const response = await mataMataService.getKnockoutBracket(id);
      const bracketData = response.data || {};
      
      // Função para calcular rankings baseada no bracket
      const calculateRankings = (data) => {
        const newRankings = {
          champion: null,
          runnerUp: null,
          thirdPlace: null
        };
        
        // Campeão e Vice-campeão
        if (data.final?.status === 'completed') {
          const { scoreA, scoreB, teamA, teamB } = data.final;
          if (typeof scoreA === 'number' && typeof scoreB === 'number') {
            newRankings.champion = scoreA > scoreB ? teamA : teamB;
            newRankings.runnerUp = scoreA > scoreB ? teamB : teamA;
          }
        }
        
        // Terceiro lugar
        if (data.thirdPlace?.status === 'completed') {
          const { scoreA, scoreB, teamA, teamB } = data.thirdPlace;
          if (typeof scoreA === 'number' && typeof scoreB === 'number') {
            newRankings.thirdPlace = scoreA > scoreB ? teamA : teamB;
          }
        }
        
        return newRankings;
      };
      
      const newRankings = calculateRankings(bracketData);
      setRankings(newRankings);
      
    } catch (err) {
      console.error('Erro ao buscar rankings:', err);
    } finally {
      setLoadingRankings(false);
    }
  }, [id]);

  // ✅ LÓGICA CORRIGIDA: FILTRA AS PARTIDAS DE HOJE
  const matchesToday = useMemo(() => {
    if (!matches || matches.length === 0) {
      return [];
    }
    
    const today = new Date();
    // Cria uma data de início do dia (00:00:00) no fuso horário local
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    
    // Cria uma data de fim do dia (23:59:59.999) no fuso horário local
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

    console.log('🔍 Filtrando partidas de hoje...');
    console.log('📅 Data de início do dia:', startOfDay.toLocaleString('pt-BR'));
    console.log('📅 Data de fim do dia:', endOfDay.toLocaleString('pt-BR'));
    console.log('📊 Total de partidas para filtrar:', matches.length);

    const todayMatches = matches.filter(match => {
      if (!match.date) {
        return false;
      }
      
      try {
        const matchDate = new Date(match.date);
        if (isNaN(matchDate.getTime())) {
          return false;
        }
        
        // Verifica se a data da partida está dentro do intervalo de hoje
        const isToday = matchDate >= startOfDay && matchDate <= endOfDay;
        
        if (isToday) {
          console.log('✅ Partida de hoje encontrada:', {
            id: match._id,
            date: matchDate.toLocaleString('pt-BR'),
            homeTeam: match.homeTeam?.name || match.teamA?.name,
            awayTeam: match.awayTeam?.name || match.teamB?.name
          });
        }
        
        return isToday;
      } catch (error) {
        console.error('❌ Erro ao processar data da partida:', match._id, error);
        return false;
      }
    });
    
    console.log('🎯 Partidas filtradas para hoje:', todayMatches.length, 'de', matches.length, 'total');
    
    // Ordena por horário
    return todayMatches.sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return dateA - dateB;
    });
  }, [matches]);

  // ✅ CORREÇÃO AQUI: Converter para string antes de comparar
  const isCurrentChampionshipManager = useMemo(() => {
    if (!user || !championship) return false;
    const userId = user._id?.toString(); 
    
    const isCreator = championship.creator?._id?.toString() === userId; 
    const isManager = championship.managers?.some(m => m._id?.toString() === userId); 
    
    return isCreator || isManager;
  }, [user, championship]);

  // ✅ NOVO: Função para verificar permissões de gerenciamento
  const canManageChampionship = useMemo(() => {
    if (loadingAuth || !user) return false;
    
    // Admin tem acesso total
    if (user.role && user.role.toLowerCase() === 'admin') return true;
    
    // Gestor ou Diretor só tem acesso se for manager do campeonato
    if (user.role && (user.role.toLowerCase() === 'gestor' || user.role.toLowerCase() === 'diretor')) {
      return isCurrentChampionshipManager;
    }
    
    return false;
  }, [user, loadingAuth, isCurrentChampionshipManager]);
  
  const categoryDisplay = useMemo(() => {
    if (!championship) return null;
    const { gender, baseCategory, customCategory } = championship;
    const parts = [];
    if (gender) parts.push(gender);
    if (baseCategory) {
      parts.push(baseCategory);
    } else if (customCategory) {
      parts.push(customCategory);
    }
    return parts.join(' - ');
  }, [championship]);

  const hasPrizes = useMemo(() => {
    if (!championship) return false;
    return !!(championship.prizeFirst || championship.prizeSecond || championship.prizeThird || championship.prizeFourth);
  }, [championship]);

  const canPerformFrontendAdminActions = useMemo(() => {
    if (loadingAuth || !user || !user.role) return false;
    if (user.role.toLowerCase() === 'admin') return true;
    if (user.role.toLowerCase() === 'gestor' && isCurrentChampionshipManager) return true;
    return false;
  }, [user, loadingAuth, isCurrentChampionshipManager]);

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const championshipHasGroupStage = useMemo(() => hasGroups(championship?.format), [championship]);

  // ✅ NOVO: Função para verificar permissões de gerenciamento da TAB
  const shouldShowManageGroupsTab = useMemo(() => {
    if (loadingAuth || !user || !championship) return false;

    const userRoleLower = user.role?.toLowerCase();
    const isChampionshipAdminOrManager = userRoleLower === 'admin' || isCurrentChampionshipManager;
    
    // A aba só aparece se o campeonato tiver fase de grupos (ou formato personalizado)
    // E se o usuário tem permissão de Admin ou é um manager do campeonato
    // Além disso, verifica se há pelo menos 2 times para gerenciar grupos (opcional)
    const hasMinimumTeams = teams.length >= 2;
    
    return championshipHasGroupStage && isChampionshipAdminOrManager && hasMinimumTeams;
  }, [loadingAuth, user, championship, championshipHasGroupStage, isCurrentChampionshipManager, teams.length]);

  // ✅ FUNÇÃO: Encontrar informações da primeira partida
  const firstMatchInfo = useMemo(() => {
    if (!matches || matches.length === 0) return null;
    
    // Encontrar a partida com a menor data (mais antiga) que tenha data válida
    const validMatches = matches.filter(match => match.date && !isNaN(new Date(match.date).getTime()));
    if (validMatches.length === 0) return null;
    
    validMatches.sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstMatch = validMatches[0];
    
    return {
      date: firstMatch.date,
      homeTeam: firstMatch.homeTeam?.name || 'Time A',
      awayTeam: firstMatch.awayTeam?.name || 'Time B',
      id: firstMatch._id
    };
  }, [matches]);

  // ✅ FUNÇÃO: Determinar informação de início relevante
  const relevantStartInfo = useMemo(() => {
    if (firstMatchInfo?.date) {
      return {
        date: firstMatchInfo.date,
        type: `a primeira partida (${firstMatchInfo.homeTeam} vs ${firstMatchInfo.awayTeam})`,
        isMatchDate: true
      };
    }
    
    if (championship?.startDate) {
      return {
        date: championship.startDate,
        type: 'o início do campeonato',
        isMatchDate: false
      };
    }
    
    return null;
  }, [firstMatchInfo, championship]);

  // ✅ FUNÇÃO ATUALIZADA: Verifica o status do campeonato CONSIDERANDO DATAS E RANKINGS
  const getChampionshipStatus = useMemo(() => {
    if (!championship) return 'scheduled';
    
    const status = championship.status?.toLowerCase() || '';
    const now = new Date();
    
    // 1. PRIORIDADE: Status oficial (finalizado) - se o administrador marcou como finalizado
    if (status.includes('concluído') || status.includes('finalizado') || status.includes('completed') || status.includes('terminado')) {
      return 'completed';
    }
    
    // 2. Verificar se tem ranking definido (campeão, vice, etc.)
    const hasRankings = rankings.champion || rankings.runnerUp || rankings.thirdPlace;
    if (hasRankings) {
      // Se já tem rankings definidos, considera finalizado mesmo sem status oficial
      return 'completed';
    }
    
    // 3. Determinar data de início relevante
    const relevantDate = relevantStartInfo?.date;
    if (!relevantDate) {
      // Se não tem nenhuma data, usa status oficial ou default
      if (status.includes('andamento') || status.includes('in-progress') || status.includes('em andamento')) {
        return 'in-progress';
      }
      return 'scheduled';
    }
    
    // 4. Comparar com data atual
    const startDate = new Date(relevantDate);
    if (now >= startDate) {
      // Já passou da data de início/primeira partida
      
      // Verificar se tem data de término
      if (championship.endDate) {
        const endDate = new Date(championship.endDate);
        if (now > endDate) {
          // Passou da data de término → finalizado
          return 'completed';
        }
      }
      
      // Se chegou aqui, está entre início e fim (ou sem data fim)
      return 'in-progress';
    } else {
      // Ainda não chegou na data de início/primeira partida
      return 'scheduled';
    }
  }, [championship, relevantStartInfo, rankings]);

  // ✅ Verificar se o campeonato tem mata-mata
  const hasKnockoutStage = useMemo(() => {
    if (!championship?.format) return false;
    const format = championship.format.toLowerCase();
    return format.includes('mata-mata') || format.includes('knockout');
  }, [championship]);

  const championshipCompleted = useMemo(() => {
    return getChampionshipStatus === 'completed';
  }, [getChampionshipStatus]);

  useEffect(() => {
    fetchData();
    // Se o campeonato tem mata-mata, buscar rankings também
    if (hasKnockoutStage) {
      fetchRankings();
    }
  }, [fetchData, fetchRankings, hasKnockoutStage]);

  useEffect(() => {
    if (loadingAuth || loading || !championship) return;

    const basePath = `/championships/${id}`;
    const currentPath = window.location.pathname;
    
    const tabSegments = [];
    tabSegments.push(null); // Visão Geral
    tabSegments.push('matches'); // Partidas
    tabSegments.push('teams'); // Times   
    if (championshipHasGroupStage) {
      tabSegments.push('standings'); // Classificação
    }
    tabSegments.push('knockout'); // Mata-Mata
    tabSegments.push('statistics'); // Estatísticas
    tabSegments.push('highlights'); // Destaques
    tabSegments.push('market'); // Mercado
    tabSegments.push('news'); // Notícias

    // AQUI: Usa a nova variável para decidir se a aba deve ser adicionada à lista de segmentos
    if (shouldShowManageGroupsTab) {
      tabSegments.push('manage-groups');
    }

    tabSegments.push('managers'); // Gestores
    tabSegments.push('sponsors'); // Patrocinadores
    tabSegments.push('media'); // Mídia
    tabSegments.push('regulations'); // Regulamento
    
    const targetPathSegment = tabSegments[activeTab];

    if (activeTab === 0) { 
      const validSubPaths = tabSegments.filter(Boolean); 
      if (currentPath !== basePath && currentPath !== `${basePath}/` && 
          !validSubPaths.some(seg => currentPath.includes(`/${seg}`))) {
        navigate(basePath);
      }
    } else if (targetPathSegment) {
      const targetPath = `${basePath}/${targetPathSegment}`;
      if (currentPath !== targetPath) {
        navigate(targetPath);
      }
    }
  }, [activeTab, id, navigate, loading, loadingAuth, championship, shouldShowManageGroupsTab, championshipHasGroupStage]);

  const handleImageError = (e) => {
    e.target.src = '/default-championship-logo.png';
    e.target.onerror = null;
  };

  const handleCopyChampionshipLink = () => {
    const championshipDetailLink = `${window.location.origin}/championships/${id}`;
    navigator.clipboard.writeText(championshipDetailLink)
      .then(() => {
        setLinkCopiedMessage('Link do Campeonato Copiado!');
        setTimeout(() => setLinkCopiedMessage(''), 3000);
      })
      .catch(err => {
        console.error('Falha ao copiar o link do campeonato:', err);
        setLinkCopiedMessage('Erro ao copiar o link!');
        setTimeout(() => setLinkCopiedMessage(''), 3000);
      });
  };
  
  // ============================================================
  // FUNÇÃO GERAR SÚMULA EM BRANCO (WEB)
  // MODIFICADA: Súmula em uma página, relatório em outra ocupando a página toda
  // ============================================================
  const handlePrintBlankSumula = () => {
    const logoUrl        = championship?.logo        || '';
    const headerImageUrl = championship?.headerImage || '';

    const playerRows = Array.from({ length: 25 }).map(() =>
      `<tr><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell" style="color:blue;">&nbsp;</td><td class="shirt-no-cell">&nbsp;</td><td class="player-name-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell" style="color:blue;">&nbsp;</td><td class="shirt-no-cell">&nbsp;</td><td class="player-name-cell">&nbsp;</td>`
    ).join('');

    const staffRows = Array.from({ length: 3 }).map(() =>
      `<tr><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="shirt-no-cell">&nbsp;</td><td class="player-name-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="card-cell">&nbsp;</td><td class="shirt-no-cell">&nbsp;</td><td class="player-name-cell">&nbsp;</td>`
    ).join('');

    const faltasCols    = Array.from({ length: 7 }).map(() => '<col style="width:2.9%;"/>').join('');
    const faltasTh      = Array.from({ length: 7 }).map((_, i) => `<th>${i+1}o</th>`).join('');
    const faltasTdBlank = Array.from({ length: 7 }).map(() => '<td class="fg-data-cell">&nbsp;</td>').join('');
    const golsCols      = Array.from({ length: 9 }).map(() => '<col style="width:3.1%;"/>').join('');
    const golsTh        = Array.from({ length: 9 }).map((_, i) => `<th>${i+1}o</th>`).join('');
    const golsTdBlank   = Array.from({ length: 9 }).map(() => '<td class="fg-data-cell">&nbsp;</td>').join('');
    // Aumenta o número de linhas do relatório para preencher melhor, mas o CSS vai garantir ocupação total
    const reportLines   = Array.from({ length: 30 }).map(() => '<div class="report-line"></div>').join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Sumula em Branco - ${championship?.name || 'Campeonato'}</title>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
    @page { margin: 4mm; size: A4; }
    body { font-family: Helvetica, Arial, sans-serif; font-size: 7pt; color: #000; margin: 0; padding: 0; background: #fff; width: 100%; overflow-x: hidden; }
    .sumula-container { max-width: 100%; overflow-x: hidden; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 2px; table-layout: fixed; }
    td, th { border: 1px solid #000; padding: 1px 2px; vertical-align: middle; word-wrap: break-word; }
    .bg-gray { background-color: #e0e0e0; font-weight: bold; text-transform: uppercase; }
    .text-center { text-align: center; }
    .bold { font-weight: bold; }
    .signature-cell { height: 18px; vertical-align: top; font-size: 6pt; border-bottom: 1px solid #000; border-top: none; border-left: none; border-right: none; }
    .custom-sumula-header { display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 6px; width: 100%; margin-bottom: 3px; flex-wrap: nowrap; }
    .sumula-logo-embedded { max-height: 28px; max-width: 60px; width: auto; height: auto; object-fit: contain; flex-shrink: 0; }
    .sumula-header-image { flex: 1; max-height: 35px; width: auto; object-fit: contain; min-width: 0; }
    .card-cell { text-align: center !important; font-weight: bold; }
    .shirt-no-cell { text-align: center !important; }
    .player-name-cell { text-align: left !important; padding-left: 3px !important; word-break: break-word; }
    .fg-data-cell { text-align: center !important; }
    .fg-row-period-label { text-align: left !important; font-weight: bold; padding-left: 2px !important; }
    .fg-header-team-name { text-align: left !important; padding-left: 2px !important; font-weight: bold !important; }
    
    /* ✅ NOVOS ESTILOS PARA O RELATÓRIO OCUPAR A PÁGINA TODA */
    .page-break-before {
      page-break-before: always;
      break-before: page;
    }
    
    /* Container do relatório ocupará toda a página */
    .sumula-report-container {
      border: 1px solid #000;
      margin-top: 0;
      display: flex;
      flex-direction: column;
      height: 100vh; /* Ocupa toda a altura da viewport na impressão */
      max-height: 100vh;
      box-sizing: border-box;
    }
    
    .report-title {
      padding: 4px;
      border-bottom: 1px solid #000;
      font-size: 10pt;
      text-align: center;
      font-weight: bold;
      background-color: #e0e0e0;
      flex-shrink: 0;
    }
    
    .report-area {
      flex: 1;
      padding: 5px;
      line-height: 1.4;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    
    .report-line {
      border-bottom: 0.5px dotted #999;
      height: 24px; /* Altura um pouco maior para ocupar espaço */
      margin: 0 0 2px 0;
    }
    
    /* Ajuste para impressão: remove margens extras e força o relatório a ocupar a página inteira */
    @media print {
      body {
        margin: 0;
        padding: 0;
        height: 100vh;
        display: block;
      }
      .sumula-report-container {
        height: 100%;
        page-break-after: avoid;
        page-break-inside: avoid;
      }
      .report-area {
        min-height: calc(100% - 30px); /* subtrai a altura do título */
      }
    }
  </style>
</head>
<body>
<div class="sumula-container">

  <div class="custom-sumula-header">
    ${logoUrl        ? `<img src="${logoUrl}"        class="sumula-logo-embedded" />` : ''}
    ${headerImageUrl ? `<img src="${headerImageUrl}" class="sumula-header-image" />`  : `<h2 style="margin:0;font-size:13pt;flex:1;text-align:center;">${championship?.name || ''}</h2>`}
  </div>

  <table><tbody>
    <tr><td colspan="2" class="bg-gray">FASE / RODADA: </td><td colspan="4" class="bg-gray text-center">SCORE</th></tr>
    <tr>
      <td class="bg-gray" style="width:12%;">Jogo:</td>
      <td colspan="3" class="bold text-center" style="width:38%;">_________________________</td>
      <td class="text-center bold" style="width:8%;font-size:11pt;">&nbsp;</td>
      <td class="text-center" style="width:4%;">X</td>
      <td class="text-center bold" style="width:8%;font-size:11pt;">&nbsp;</td>
      <td colspan="2" class="bold text-center" style="width:30%;">_________________________</td>
    </tr>
    <tr>
      <td class="bg-gray">Data:</td><td class="bold text-center">____/____/________</td>
      <td class="bg-gray" style="width:12%;">Horario:</td><td class="bold text-center">____:____</td>
      <td colspan="5" class="bg-gray">Local: <span class="bold">_________________________</span></td>
    </tr>
    <tr>
      <td class="bg-gray">Arbitro:</td><td colspan="3">_________________________</td>
      <td class="bg-gray" colspan="2">Delegado:</td><td colspan="3">_________________________</td>
    </tr>
    <tr><td class="bg-gray" colspan="2">Arbitro Principal:</td><td colspan="7">_________________________</td></tr>
  </tbody></table>

  <table>
    <colgroup>
      <col style="width:4%;"/><col style="width:4%;"/><col style="width:4%;"/><col style="width:5%;"/><col style="width:33%;"/>
      <col style="width:4%;"/><col style="width:4%;"/><col style="width:4%;"/><col style="width:5%;"/><col style="width:33%;"/>
    </colgroup>
    <thead>
      <tr class="bg-gray text-center">
        <th colspan="4">INFO</th><th>EQUIPE MANDANTE</th>
        <th colspan="4">INFO</th><th>EQUIPE VISITANTE</th>
      </tr>
      <tr class="bg-gray text-center">
        <th>A</th><th>V</th><th>G</th><th>No</th><th>NOME DOS JOGADORES</th>
        <th>A</th><th>V</th><th>G</th><th>No</th><th>NOME DOS JOGADORES</th>
      </tr>
    </thead>
    <tbody>
      ${playerRows}
      <tr><td colspan="10" style="border-bottom:1px solid #666;height:1mm;padding:0;"></td></tr>
      ${staffRows}
    </tbody>
  </table>

  <table><tbody>
    <tr><td class="signature-cell" style="width:50%;">No e Ass. Capitao:</td><td class="signature-cell" style="width:50%;">No e Ass. Capitao:</td></tr>
    <tr><td class="signature-cell">Tecnico: </td><td class="signature-cell">Tecnico: </td></tr>
  </tbody></table>

  <table>
    <colgroup><col style="width:17%;"/>${faltasCols}<col style="width:4.5%;"/><col style="width:17%;"/>${faltasCols}<col style="width:4.5%;"/></colgroup>
    <thead>
      <tr class="bg-gray text-center"><th colspan="9">FALTAS</th><th colspan="9">FALTAS</th></tr>
      <tr>
        <th class="fg-header-team-name">MANDANTE</th>${faltasTh}<th>Total</th>
        <th class="fg-header-team-name">VISITANTE</th>${faltasTh}<th>Total</th>
      </tr>
    </thead>
    <tbody>
      <tr><td class="fg-row-period-label">1o Tempo</td>${faltasTdBlank}<td class="fg-data-cell">&nbsp;</td><td class="fg-row-period-label">1o Tempo</td>${faltasTdBlank}<td class="fg-data-cell">&nbsp;</td></tr>
      <tr><td class="fg-row-period-label">2o Tempo</td>${faltasTdBlank}<td class="fg-data-cell">&nbsp;</td><td class="fg-row-period-label">2o Tempo</td>${faltasTdBlank}<td class="fg-data-cell">&nbsp;</td></tr>
    </tbody>
  </table>

  <table style="margin-top:5px;">
    <colgroup><col style="width:17%;"/>${golsCols}<col style="width:17%;"/>${golsCols}</colgroup>
    <thead>
      <tr class="bg-gray text-center"><th colspan="10">GOLS - MANDANTE</th><th colspan="10">GOLS - VISITANTE</th></tr>
      <tr>
        <th class="fg-header-team-name">TEMPO</th>${golsTh}
        <th class="fg-header-team-name">TEMPO</th>${golsTh}
      </tr>
    </thead>
    <tbody>
      <tr><td class="fg-row-period-label">1o T</td>${golsTdBlank}<td class="fg-row-period-label">1o T</td>${golsTdBlank}</tr>
      <tr><td class="fg-row-period-label">2o T</td>${golsTdBlank}<td class="fg-row-period-label">2o T</td>${golsTdBlank}</tr>
      <tr>
        <td colspan="10" style="vertical-align:top;padding:3px;"><div style="font-weight:bold;border-bottom:0.5px solid #eee;">ARTILHEIROS:</div></td>
        <td colspan="10" style="vertical-align:top;padding:3px;"><div style="font-weight:bold;border-bottom:0.5px solid #eee;">ARTILHEIROS:</div></td>
      </tr>
    </tbody>
  </table>

  <table style="margin-top:3px;">
    <colgroup><col style="width:20%;"/><col style="width:30%;"/><col style="width:20%;"/><col style="width:30%;"/></colgroup>
    <thead><tr class="bg-gray text-center"><th colspan="2">TEMPO TECNICO - MANDANTE</th><th colspan="2">TEMPO TECNICO - VISITANTE</th></tr></thead>
    <tbody>
      <tr><td class="fg-row-period-label" style="text-align:center;">1o Tempo</td><td class="fg-data-cell" style="height:16px;">&nbsp;</td><td class="fg-row-period-label" style="text-align:center;">1o Tempo</td><td class="fg-data-cell" style="height:16px;">&nbsp;</td></tr>
      <tr><td class="fg-row-period-label" style="text-align:center;">2o Tempo</td><td class="fg-data-cell" style="height:16px;">&nbsp;</td><td class="fg-row-period-label" style="text-align:center;">2o Tempo</td><td class="fg-data-cell" style="height:16px;">&nbsp;</td></tr>
    </tbody>
  </table>

  <!-- ✅ RELATÓRIO DE ARBITRAGEM COM SALTO DE PÁGINA E OCUPAÇÃO TOTAL -->
  <div class="page-break-before"></div>
  <div class="sumula-report-container">
    <div class="report-title">RELATORIO DE ARBITRAGEM</div>
    <div class="report-area">
      ${reportLines}
      <!-- Espaço extra para preencher caso necessário -->
      <div style="flex:1;"></div>
    </div>
  </div>

</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Permita pop-ups para gerar a sumula.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  // ============================================================
  // FUNÇÃO GERAR FICHA DE INSCRIÇÃO EM BRANCO (WEB)
  // ============================================================
  const handlePrintBlankRegistrationSheet = () => {
    const championshipNameUpper = championship?.name?.toUpperCase() || 'CAMPEONATO';
    const logoUrl = championship?.logo || '';
    const headerImageUrl = championship?.headerImage || '';

    const MAX_PLAYERS  = 25;
    const MAX_TECHNICAL = 5;

    const techRows = Array.from({ length: MAX_TECHNICAL }).map((_, idx) => `
      <tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">
        <td class="col-num">${idx + 1}</td>
        <td class="col-name"></td>
        <td></td>
        <td class="col-rg"></td>
        <td class="col-sig"></td>
       </tr>`).join('');

    const playerRows = Array.from({ length: MAX_PLAYERS }).map((_, idx) => `
      <tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">
        <td class="col-num">${idx + 1}</td>
        <td class="col-name"></td>
        <td class="col-rg"></td>
        <td class="col-sig"></td>
       </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Ficha de Inscrição em Branco – ${championship?.name || 'Campeonato'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4 portrait; margin: 12mm 14mm 12mm 14mm; }
    html, body { width: 210mm; min-height: 297mm; font-family: Arial, sans-serif; background: #fff; color: #1a1a1a; }
    .sheet { width: 100%; min-height: 277mm; display: flex; flex-direction: column; }

    /* Header */
    .header-banner img { width: 100%; height: auto; max-height: 44px; object-fit: contain; display: block; margin-bottom: 6px; }
    .header-main { display: flex; align-items: center; justify-content: space-between; border-top: 3px solid #1a3a6b; border-bottom: 3px solid #1a3a6b; padding: 7px 0; }
    .logo-box { width: 58px; height: 58px; border: 1.5px solid #c8c8c8; border-radius: 6px; overflow: hidden; background: #f5f5f5; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .logo-box img { width: 100%; height: 100%; object-fit: contain; }
    .logo-placeholder { width: 58px; height: 58px; background: #eee; border-radius: 6px; }
    .title-block { flex: 1; text-align: center; padding: 0 10px; }
    .main-title { font-size: 13pt; font-weight: 900; letter-spacing: 0.5px; color: #1a3a6b; text-transform: uppercase; line-height: 1.2; }
    .champ-name { font-size: 9.5pt; font-weight: 700; color: #444; margin-top: 3px; letter-spacing: 0.3px; }

    /* Team Info */
    .team-info { display: flex; gap: 0; border: 1.5px solid #b0b8c9; border-radius: 5px; margin: 9px 0 10px; overflow: hidden; }
    .info-cell { flex: 1; padding: 5px 8px; border-right: 1px solid #c8d0dc; }
    .info-cell:last-child { border-right: none; }
    .info-label { font-size: 6.8pt; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 3px; }
    .info-value { font-size: 9pt; border-bottom: 1px solid #aaa; height: 16px; }

    /* Section */
    .section { margin-bottom: 9px; }
    .section-title { font-size: 8pt; font-weight: 800; color: #fff; background: #1a3a6b; padding: 3px 8px; letter-spacing: 0.8px; text-transform: uppercase; border-radius: 3px 3px 0 0; }

    /* Tables */
    table { width: 100%; border-collapse: collapse; font-size: 7.8pt; }
    thead tr { background: #dce4f0; }
    th { padding: 4px 5px; text-align: center; font-weight: 800; border: 1px solid #aab4c8; color: #1a3a6b; font-size: 7.5pt; letter-spacing: 0.3px; }
    td { padding: 2.5px 5px; border: 1px solid #c8d0dc; vertical-align: middle; height: 20px; }
    .row-even { background: #fff; }
    .row-odd  { background: #f2f5fb; }
    .col-num  { width: 6%; text-align: center; font-weight: 700; color: #555; }
    .col-name { width: 48%; }
    .col-rg   { width: 28%; }
    .col-sig  { width: 18%; }

    /* Footer */
    .footer { margin-top: auto; padding-top: 10px; border-top: 2px solid #1a3a6b; display: flex; justify-content: space-between; align-items: flex-end; }
    .sig-block { display: flex; flex-direction: column; gap: 3px; }
    .sig-line { border-bottom: 1.5px solid #333; width: 230px; height: 18px; }
    .sig-label { font-size: 7.5pt; font-weight: 700; color: #555; }
    .date-block { font-size: 8.5pt; font-weight: 700; color: #333; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
<div class="sheet">

  <div class="header">
    ${headerImageUrl ? `<div class="header-banner"><img src="${headerImageUrl}" /></div>` : ''}
    <div class="header-main">
      <div class="logo-box">
        ${logoUrl ? `<img src="${logoUrl}" alt="Logo do campeonato" />` : `<div class="logo-placeholder"></div>`}
      </div>
      <div class="title-block">
        <div class="main-title">Ficha de Inscrição de Atletas</div>
        <div class="champ-name">${championshipNameUpper}</div>
      </div>
      <div class="logo-box">
        ${logoUrl ? `<img src="${logoUrl}" alt="Logo do campeonato" />` : `<div class="logo-placeholder"></div>`}
      </div>
    </div>
  </div>

  <div class="team-info">
    <div class="info-cell"><div class="info-label">Nome da Equipe</div><div class="info-value"></div></div>
    <div class="info-cell"><div class="info-label">Responsável</div><div class="info-value"></div></div>
    <div class="info-cell"><div class="info-label">Contato</div><div class="info-value"></div></div>
  </div>

  <div class="section">
    <div class="section-title">Comissão Técnica</div>
    <table>
      <thead><tr>
        <th style="width:6%">Nº</th>
        <th style="width:34%">Nome Completo</th>
        <th style="width:19%">Função</th>
        <th style="width:23%">CPF</th>
        <th style="width:18%">Assinatura</th>
       </tr></thead>
      <tbody>${techRows}</tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Jogadores</div>
    <table>
      <thead><tr>
        <th class="col-num">Nº</th>
        <th class="col-name">Nome Completo do Atleta</th>
        <th class="col-rg">CPF</th>
        <th class="col-sig">Assinatura</th>
       </tr></thead>
      <tbody>${playerRows}</tbody>
    </table>
  </div>

  <div class="footer">
    <div class="sig-block">
      <div class="sig-line"></div>
      <div class="sig-label">Assinatura do Responsável pela Equipe</div>
    </div>
    <div class="date-block">Data: ______ / ______ / __________</div>
  </div>

</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Permita pop-ups para gerar a ficha de inscrição.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
    };
  };

  const allowedRegistrationRoles = useMemo(() => ['diretor', 'gestor', 'admin', 'tecnico'], []);

  const canRegisterTeam = useMemo(() => {
    if (loadingAuth || !user || !user.role) return false;
    return allowedRegistrationRoles.includes(user.role.toLowerCase());
  }, [user, loadingAuth, allowedRegistrationRoles]);

  const championshipProgress = useMemo(() => {
     if (!matches || matches.length === 0) {
      return { percentage: 0, completed: 0, total: 0 };
    }
    const completedMatches = matches.filter(match => 
      match.status && match.status.toLowerCase() === 'completed'
    ).length;
    const totalMatches = matches.length;
    const percentage = totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0;
    return { percentage, completed: completedMatches, total: totalMatches };
  }, [matches]);

  const getFirstName = () => {
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    if (user?.username) {
      return user.username;
    }
    return 'Usuário';
  };

  // ✅ Determina o título baseado no status
  const getStatusTitle = useCallback(() => {
    const status = getChampionshipStatus;
    
    switch(status) {
      case 'completed':
        return 'Campeonato finalizado';
      case 'in-progress':
        return 'Bola rolando';
      default:
        return 'Aguardando início';
    }
  }, [getChampionshipStatus]);

  // ✅ Determina a classe CSS baseada no status
  const getStatusContainerClass = useCallback(() => {
    const status = getChampionshipStatus;
    
    switch(status) {
      case 'completed':
        return 'status-completed';
      case 'in-progress':
        return 'status-in-progress';
      default:
        return 'status-scheduled';
    }
  }, [getChampionshipStatus]);

  // ✅ LÓGICA DO BOTÃO DE INSCRIÇÃO ATUALIZADA COM O CAMPO registrationOpen
  const shouldShowRegisterButton = useMemo(() => {
    if (!championship || !canRegisterTeam) return false;

    // Verificar se as inscrições estão abertas (campo registrationOpen)
    const isRegistrationOpen = championship.registrationOpen === true;
    
    // Verificar se atingiu o limite máximo de times
    const maxTeamsReached = championship.maxTeams && teams.length >= championship.maxTeams;
    
    // Verificar se o campeonato ainda não começou
    const statusIsScheduled = getChampionshipStatus === 'scheduled';
    
    // Todas as condições devem ser verdadeiras
    return isRegistrationOpen && canRegisterTeam && !maxTeamsReached && statusIsScheduled;

  }, [championship, canRegisterTeam, teams.length, getChampionshipStatus]);

  if (loadingAuth || (loading && !championship)) return <LoadingSpinner />;

  if (error && !championship) {
     return (
      <div className="error-container">
        <h3>Erro ao carregar campeonato</h3>
        <p>{error}</p>
        <Link to="/championships" className="back-link">Voltar para a lista de campeonatos</Link>
      </div>
    );
  }
  
  if (!championship || typeof championship !== 'object') {
     return (
      <div className="not-found">
        <h3>Campeonato não encontrado ou dados inválidos.</h3>
        <Link to="/championships" className="back-link">Voltar para a lista de campeonatos</Link>
      </div>
    );
  }
  
  const hasMaxTeamsReached = championship.maxTeams && teams.length >= championship.maxTeams;

  return (
    <div className="championship-detail-container">
      <div
        className="detail-background"
        style={{ backgroundImage: `url(${backgroundImg})` }}
        aria-hidden="true"
      />

      <div className="detail-topbar">
        <Link to="/championships" className="back-to-list">
          <ChevronLeftIcon />
          Campeonatos
        </Link>
        {!loadingAuth && user && (
          <p className="welcome-message-bar">
            Bem-vindo, <strong>{getFirstName()}</strong>! ({user.role})
          </p>
        )}
      </div>

      <div className="championship-header">
        <img
          src={championship.logo || '/default-championship-logo.png'}
          alt={`${championship.name} logo`}
          className="detail-logo"
          onError={handleImageError}
        />
        <div className="detail-info">
          <h1>{championship.name}</h1>
          <div className="meta-info">
            {categoryDisplay && (
              <span className="category">{categoryDisplay}</span>
            )}
            <span className="location">{championship.location}</span>
            <span className="format">{championship.format}</span>
            <span className="date">Início: {formatDateOnly(championship.startDate)}</span>
            <span className={`status ${normalizeStatus(championship.status)}`}>
              {championship.status || 'Status não informado'}
            </span>
            {/* ✅ INDICADOR DE STATUS DAS INSCRIÇÕES */}
            {championship.registrationOpen !== undefined && (
              <span className={`registration-status ${championship.registrationOpen ? 'open' : 'closed'}`}>
                {championship.registrationOpen ? 'Inscrições abertas' : 'Inscrições fechadas'}
              </span>
            )}
          </div>
          {canManageChampionship && (
            <Link to={`/championships/${id}/edit`} className="edit-button">
              Editar Campeonato
            </Link>
          )}
           {error && <p className="error-message-inline">Aviso: {error}</p>}
        </div>
      </div>

      <Tabs
        selectedIndex={activeTab}
        onSelect={(index) => setActiveTab(index)}
        className="championship-tabs"
      >
        <TabList className="tab-list">
          <Tab className="tab">Visão Geral</Tab>
          <Tab className="tab">Partidas</Tab>
          <Tab className="tab">Times</Tab>
          {championshipHasGroupStage && <Tab className="tab">Classificação</Tab>}
          <Tab className="tab">Mata-Mata</Tab>
          <Tab className="tab">Estatísticas</Tab>
          <Tab className="tab">Destaques</Tab>
          <Tab className="tab">Mercado</Tab>
          <Tab className="tab">Notícias</Tab>
          
          {/* AQUI: Renderiza a aba de gerenciamento de grupos e partidas
             usando a nova variável shouldShowManageGroupsTab */}
          {shouldShowManageGroupsTab && <Tab className="tab admin-tab">Gerenciar Grupos e Partidas</Tab>}

          <Tab className="tab">Gestores</Tab>
          <Tab className="tab">Patrocinadores</Tab>
          <Tab className="tab">Mídia</Tab>
          <Tab className="tab">Regulamento</Tab>
          {isAdmin && <Tab className="tab admin-tab">Tribunal</Tab>}
        </TabList>

        <div className="detail-toolbar">
          <button onClick={handleCopyChampionshipLink} className="btn-copiar-link-campeonato">
            Copiar link
          </button>
          <button onClick={handlePrintBlankSumula} className="btn-sumula-branco">
            Súmula em branco
          </button>
          <button onClick={handlePrintBlankRegistrationSheet} className="btn-ficha-inscricao">
            Ficha em branco
          </button>
          {linkCopiedMessage && (
            <span role="status" className={`link-copied-message ${linkCopiedMessage.startsWith('Erro') ? 'error' : 'success'}`}>
              {linkCopiedMessage}
            </span>
          )}
          {/* ✅ BOTÃO DE INSCREVA-SE - AGORA RESPONSIVO AO CAMPO registrationOpen */}
          {shouldShowRegisterButton && (
            <Link to={`/championships/${id}/teams/register`} className="btn-inscreva-se">
              Inscreva-se
            </Link>
          )}
          {/* ✅ INDICAÇÃO VISUAL QUANDO AS INSCRIÇÕES ESTÃO FECHADAS */}
          {championship && championship.registrationOpen === false && canRegisterTeam && !hasMaxTeamsReached && getChampionshipStatus === 'scheduled' && (
            <span className="registration-closed-indicator">
              Inscrições temporariamente fechadas
            </span>
          )}
        </div>

        <TabPanel className="tab-panel tab-panel-overview">
          <div className="overview-content">
            {/* ✅ STATUS DO CAMPEONATO - COM NOVA LÓGICA */}
            {championship && (
              <div className={`status-container card-style ${getStatusContainerClass()}`}>
                <h3 className="status-title">{getStatusTitle()}</h3>
                <ChampionshipStatusDisplay 
                  championship={championship} 
                  currentStatus={getChampionshipStatus}
                  firstMatchInfo={firstMatchInfo}
                  relevantStartInfo={relevantStartInfo}
                />
                <div className="status-details">
                  {/* Informações de datas */}
                  <div className="status-detail">
                    <span className="detail-label">Critério usado:</span>
                    <span className="detail-value">
                      {getChampionshipStatus === 'completed' && rankings.champion ? 'Ranking definido (campeão)' :
                       getChampionshipStatus === 'completed' && championship.endDate ? 'Data de término ultrapassada' :
                       getChampionshipStatus === 'completed' ? 'Status oficial finalizado' :
                       getChampionshipStatus === 'in-progress' && firstMatchInfo ? 'Primeira partida já ocorreu' :
                       getChampionshipStatus === 'in-progress' ? 'Data de início já passou' :
                       'Aguardando início'}
                    </span>
                  </div>
                  
                  {relevantStartInfo && (
                    <div className="status-detail">
                      <span className="detail-label">Referência:</span>
                      <span className="detail-value">
                        {relevantStartInfo.type}
                        {relevantStartInfo.date && ` em ${formatDate(relevantStartInfo.date)}`}
                      </span>
                    </div>
                  )}
                  
                  {championship.endDate && (
                    <div className="status-detail">
                      <span className="detail-label">Término previsto:</span>
                      <span className="detail-value">{formatDate(championship.endDate)}</span>
                    </div>
                  )}
                  
                  {/* Status oficial */}
                  <div className="status-detail">
                    <span className="detail-label">Status oficial:</span>
                    <span className={`detail-value status-${normalizeStatus(championship.status)}`}>
                      {championship.status || 'Não informado'}
                    </span>
                  </div>
                  
                  {/* Status calculado */}
                  <div className="status-detail">
                    <span className="detail-label">Status calculado:</span>
                    <span className="detail-value">
                      {getChampionshipStatus === 'scheduled' ? 'Agendado (não começou)' :
                       getChampionshipStatus === 'in-progress' ? 'Em andamento' :
                       'Finalizado'}
                    </span>
                  </div>

                  {/* ✅ STATUS DAS INSCRIÇÕES */}
                  <div className="status-detail">
                    <span className="detail-label">Inscrições:</span>
                    <span className={`detail-value ${championship.registrationOpen ? 'text-success' : 'text-danger'}`}>
                      {championship.registrationOpen ? 'Abertas' : 'Fechadas'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* ✅ INTEGRAÇÃO DO NOVO COMPONENTE AQUI */}
            {/* Apenas mostra a seção se houver partidas no geral */}
            {matches.length > 0 && (
              <MatchesToday matches={matchesToday} championshipId={id} />
            )}
            
            {/* ✅ PÓDIO DO CAMPEONATO */}
            {championshipCompleted && (rankings.champion || rankings.runnerUp || rankings.thirdPlace) && (
              <div className="podium-section">
                <ChampionshipPodium rankings={rankings} />
              </div>
            )}
            
            <div className="grid-layout">
              <div className="progress-card card-style">
                <h3>Progresso do Campeonato</h3>
                {matches.length > 0 ? (
                  <>
                    <p>{championshipProgress.completed} de {championshipProgress.total} partidas concluídas ({Math.round(championshipProgress.percentage)}%).</p>
                    <ProgressBar
                      percentage={championshipProgress.percentage}
                      color={getChampionshipStatus === 'completed' ? 'var(--gold)' :
                             getChampionshipStatus === 'in-progress' ? 'var(--live)' : 'var(--accent)'}
                    />
                    {firstMatchInfo && (
                      <p className="muted-text">
                        Primeira partida: {firstMatchInfo.homeTeam} vs {firstMatchInfo.awayTeam} ({formatDateOnly(firstMatchInfo.date)})
                      </p>
                    )}
                  </>
                ) : (
                  <p>Nenhuma partida agendada para calcular o progresso.</p>
                )}
                {championship.description && <p className="card-description">{championship.description}</p>}
              </div>
              
              <div className="teams-card card-style">
                <h3>Times Participantes</h3>
                <p>{teams.length} {teams.length === 1 ? 'time' : 'times'} inscritos</p>
                {championship.maxTeams && <p>Limite de times: {championship.maxTeams}</p>}
                {hasMaxTeamsReached && (
                  <p className="warning-message-inline">Limite de times atingido</p>
                )}
                {/* ✅ INDICADOR DE INSCRIÇÕES NO CARD DE TIMES */}
                {championship.registrationOpen !== undefined && (
                  <p className={`registration-indicator ${championship.registrationOpen ? 'open' : 'closed'}`}>
                    {championship.registrationOpen ? 'Inscrições abertas' : 'Inscrições encerradas'}
                  </p>
                )}
                {teams.length === 0 && championshipHasGroupStage && (
                    <p className="warning-message-inline">Adicione times para gerar grupos.</p>
                )}
              </div>

              <div className="athletes-card card-style">
                <h3>Atletas Inscritos</h3>
                {championship.playerCount > 0 ? (
                  <p>
                    <strong>{championship.playerCount}</strong>{' '}
                    {championship.playerCount === 1 ? 'atleta cadastrado' : 'atletas cadastrados'} no campeonato.
                  </p>
                ) : (
                  <p>Ainda não há atletas cadastrados nos times deste campeonato.</p>
                )}
                <p className="form-hint">
                  A contagem é baseada nos jogadores vinculados aos times participantes.
                </p>
              </div>

              {hasPrizes && (
                <div className="prizes-card card-style">
                  <h3>Premiação</h3>
                  <ul className="prize-list">
                    {championship.prizeFirst && <li><strong>1º Lugar:</strong> {championship.prizeFirst}</li>}
                    {championship.prizeSecond && <li><strong>2º Lugar:</strong> {championship.prizeSecond}</li>}
                    {championship.prizeThird && <li><strong>3º Lugar:</strong> {championship.prizeThird}</li>}
                    {championship.prizeFourth && <li><strong>4º Lugar:</strong> {championship.prizeFourth}</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </TabPanel>
         
        <TabPanel className="tab-panel"><div>Conteúdo da Aba Partidas</div></TabPanel>
        <TabPanel className="tab-panel">
          <div className="card-style">
            <h3>Times Participantes</h3>
            {teams.length === 0 ? (
              <p className="empty-list-message">Nenhum time inscrito neste campeonato.</p>
            ) : (
              <ul className="teams-list-tab">
                {teams.map(team => (
                  <li key={team._id} className="team-list-item">
                    <Link to={`/teams/${team._id}?championshipId=${id}`}>
                      {team.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabPanel>
        {championshipHasGroupStage && <TabPanel className="tab-panel"><div>Conteúdo da Aba Classificação</div></TabPanel>}
        <TabPanel className="tab-panel"><div>Conteúdo da Aba Mata-Mata</div></TabPanel>
        <TabPanel className="tab-panel"><div>Conteúdo da Aba Estatísticas</div></TabPanel>
        <TabPanel className="tab-panel"><RoundStats championshipId={id} /></TabPanel>
        <TabPanel className="tab-panel"><MarketList championshipId={id} /></TabPanel>
        <TabPanel className="tab-panel"><ChampionshipNews championshipId={id} /></TabPanel>
        
        {/* AQUI: Renderiza o TabPanel para Gerenciar Grupos e Partidas
           usando a nova variável shouldShowManageGroupsTab */}
        {shouldShowManageGroupsTab && (
            <TabPanel className="tab-panel">
                <ManageGroups championship={championship} onDataChange={fetchData} />
            </TabPanel>
        )}
        
        <TabPanel className="tab-panel">
          {canManageChampionship && championship?._id ? (
            <ChampionshipManagers championship={championship} />
          ) : (
            <div className="card-style">
              {loading ? <p>Carregando...</p> : <p>Você não tem permissão para acessar esta área.</p>}
            </div>
          )}
        </TabPanel>
        
        <TabPanel className="tab-panel"><SponsorList championshipId={id} isAdmin={canManageChampionship} /></TabPanel>
        <TabPanel className="tab-panel"><ChampionshipMediaTab championshipId={id} /></TabPanel>
        <TabPanel className="tab-panel"><ChampionshipRegulations championshipId={id} /></TabPanel>
        {isAdmin && (
          <TabPanel className="tab-panel">
            <PenaltyTab championshipId={id} />
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
}

ChampionshipDetail.propTypes = {
  id: PropTypes.string
};

export default ChampionshipDetail;